import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { postNotification } from '../../../../models/LocalNotifications';
import { useEventLogging } from '../../../../models/Logging';
import { accountHasComplianceEnabled } from '../../../../models/UiUtils';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useCeteraAIDisclaimer } from '../../../../models/hooks/useCeteraAIDisclaimer';
import {
	useBlogGenerationMutation,
	useBlogUpdateMutation,
	useDeleteBlogPostMutation,
	useGetSystemJobQuery,
} from '../../../../queries';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../components/ConfirmationDialog';
import { ComplianceApprovalPrompt } from '../../../components/campaigns/ComplianceApprovalPrompt';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../styles/styles';
import { CampaignSessionManager } from '../../shared/AIScheduleSuccess/campaignSessionManager';
import {
	campaignSessionStore,
	clearGlobalAISourceData,
	shouldOpenAIDrawer,
} from '../../shared/AIScheduleSuccess/campaignSessionStore';
import { UniversalAssistantChatWithinComposer } from '../../templates/AssistantChat';
import { BlogPostEditor } from './BlogPostEditor';
import { LeoFullscreenThinkingModal } from './LeoFullscreenThinkingModal';
import { BlogPostEditorContext, REDUCER_ACTION_KEYS, useBlogPostEditingRequest, validateContent } from './hooks';
import { IBlogPostComponentProps } from './models';
import { BlogPostEditorLayout } from './presentation';
import { styleSheet } from './styles';

function CreateBlogPostFromTemplateBase({
	onScheduleClicked,
	impersonationContext,
}: IBlogPostComponentProps & IImpersonationContextComponentProps) {
	const fullscreenModal = useFullscreenModal();

	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('CreateBlogPostFromTemplate');
	const userSession = useUserSession();
	const requiresCompliance = accountHasComplianceEnabled(userSession, impersonationContext);
	const showCeteraDisclaimer = useCeteraAIDisclaimer();

	const [showingAIAssistant, setShowingAIAssistant] = React.useState(() => {
		const shouldOpen = shouldOpenAIDrawer();
		if (shouldOpen) {
			clearGlobalAISourceData();
		}
		return shouldOpen;
	});

	const sourceContent = campaignSessionStore.aiSourceContent;
	const sourceType = campaignSessionStore.aiSourceType;

	const handleModalClose = React.useCallback(() => {
		CampaignSessionManager.clearSession();
		fullscreenModal.dismissModal();
	}, [fullscreenModal]);

	const { postEditorContext, blogPost, setBlogPost, request } = useBlogPostEditingRequest({
		impersonationContext,
	});
	const templateCard = request?.templateCard || request?.template;
	const { state, dispatch } = postEditorContext;

	const updateBlogPostMutation = useBlogUpdateMutation({
		impersonationContext: impersonationContext?.toJs(),
		onSuccess: blog => {
			setBlogPost(blog);
			toast.success('Blog post saved');
			postNotification({
				info: blogPost,
				topic: Topics.EDIT_BLOG_POST,
			});
		},
	});

	const accountHasAiGeneratedContentFeatureEnabled = (
		impersonationContext?.isValid ? impersonationContext.account : userSession.account
	).features?.contentGeneration?.enabled;

	const deleteBlogPostMutation = useDeleteBlogPostMutation({
		impersonationContext: impersonationContext?.toJs(),
		onSuccess: () => {
			postNotification({
				info: blogPost,
				topic: Topics.EDIT_BLOG_POST,
			});
			CampaignSessionManager.clearSession();
			fullscreenModal.dismissModal();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const blogGenerationMutation = useBlogGenerationMutation({
		impersonationContext: impersonationContext?.toJs(),
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to generate blog');
			logApiError('CreateBlogPost-Error', error);
		},
		onSuccess: (val: Api.IBlogGenerationSystemJob) => {
			if (val.generatedContent) {
				dispatch({
					type: REDUCER_ACTION_KEYS.UPDATE_WITH_BLOG_POST,
					payload: val.generatedContent.content,
				});
				showCeteraDisclaimer();
			} else {
				dispatch({ type: REDUCER_ACTION_KEYS.BLOG_GENERATION_START, payload: { id: val.id } });
			}
		},
	});

	const isSystemJobQueryEnabled = Boolean(state.blogGenerationJobId);
	const systemJobQuery = useGetSystemJobQuery<Api.IBlogGenerationSystemJob>({
		enabled: isSystemJobQueryEnabled,
		jobId: state.blogGenerationJobId,
		refetchInterval: query => {
			const data = query.state.data;
			if (!state.blogGenerationJobId) {
				return false;
			}
			return !data?.generatedContent ? 3000 : false;
		},
		refetchOnWindowFocus: false,
	});

	React.useEffect(() => {
		if (systemJobQuery.data) {
			if (systemJobQuery.data.generatedContent) {
				dispatch({
					type: REDUCER_ACTION_KEYS.SYSTEM_JOB_SUCCESS,
					payload: systemJobQuery.data.generatedContent.content,
				});
				showCeteraDisclaimer();
			}
			if (systemJobQuery.data.percentComplete === 100 && !systemJobQuery.data.generatedContent) {
				toast.error('Blog generation failed');
				dispatch({ type: REDUCER_ACTION_KEYS.BLOG_GENERATION_FAILED });
			}
		}
	}, [systemJobQuery.data, dispatch, showCeteraDisclaimer]);

	React.useEffect(() => {
		if (templateCard) {
			dispatch({ type: REDUCER_ACTION_KEYS.IMAGE_SELECTED, payload: { image: templateCard.attachments?.[0] } });
		}
	}, [dispatch, templateCard]);

	React.useEffect(() => {
		if (!blogPost) {
			blogGenerationMutation.mutate({
				template: {
					prompt: templateCard?.content,
					id: templateCard?.id,
				},
			});
			return;
		}

		dispatch({
			type: REDUCER_ACTION_KEYS.UPDATE_WITH_BLOG_POST,
			payload: blogPost,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleScheduleClick = (sendWithCompliance = false, sendWithComplianceEmail?: string) => {
		const errors = validateContent(state);
		if (errors.length !== 0) {
			errorMessages.push({
				messages: errors,
			});
			return;
		}

		const postToSave: Api.IBlogPost = {
			...blogPost,
			content: state.content.getRawRichTextContent(),
			title: state.title,
			mainImage: state.selectedImage,
			id: state.blogId,
			templateId: templateCard?.id,
			sendFromUserId: state.sendFromUser?.id,
			scheduledSendDate: blogPost?.scheduledSendDate || request?.schedule?.startDate,
			sendWithCompliance,
			sendWithComplianceEmail,
		};

		setBlogPost(postToSave); // context

		if (onScheduleClicked?.(postToSave)) {
			return;
		}
		fullscreenModal.history.push({
			pathname: '/blog/post/schedule',
		});
	};

	const handleSaveClick = () => {
		const postToSave: Api.IBlogPost = {
			content: state.content.getRawRichTextContent(),
			title: state.title,
			mainImage: state.selectedImage,
			id: state.blogId,
			templateId: templateCard.id,
			sendFromUserId: state.sendFromUser?.id,
			status: state?.status,
		};

		updateBlogPostMutation.mutate({ post: postToSave });
	};

	const handleDeleteClick = () => {
		dispatch({ type: REDUCER_ACTION_KEYS.DELETE_BLOG_POST, payload: { value: true } });
	};
	const onDeleteConfirmationRequestClose = React.useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			if (result?.isDestructive && !cancel) {
				deleteBlogPostMutation.mutate({ id: state.blogId });
				dispatch({ type: REDUCER_ACTION_KEYS.DELETE_BLOG_POST, payload: { value: false } });
			}
		},
		[deleteBlogPostMutation, dispatch, state.blogId]
	);
	const onUseAIBlog = (title: string, content: Api.IRawRichTextContentState) => {
		dispatch({
			type: REDUCER_ACTION_KEYS.USE_AI_BLOG,
			payload: {
				title,
				content,
			},
		});
	};

	return (
		<BlogPostEditorContext.Provider value={postEditorContext}>
			<BlogPostEditorLayout
				status={state.status}
				header='Generate Blog'
				onClose={handleModalClose}
				subHeader={
					<>
						<h1>New Blog</h1>
						<menu className={css(styleSheet.menu)}>
							{impersonationContext?.isValid ? null : (
								<li>
									<button type='button' className={css(baseStyleSheet.ctaButtonReverseSmall)} onClick={handleSaveClick}>
										Save Blog Post
									</button>
								</li>
							)}
							{state.blogId ? (
								<li>
									<button
										type='button'
										className={css(baseStyleSheet.ctaButtonDestructiveSmall)}
										onClick={handleDeleteClick}
									>
										Delete
									</button>
								</li>
							) : null}
						</menu>
					</>
				}
				sidebar={
					<>
						{!showingAIAssistant && requiresCompliance ? (
							<ComplianceApprovalPrompt
								campaignType={CampaignType.Blog}
								onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
									e.preventDefault();
									handleScheduleClick(true, sendWithComplianceEmail);
								}}
								onScheduleWithoutCompliance={() => handleScheduleClick()}
							/>
						) : null}
						{!showingAIAssistant && !requiresCompliance ? (
							<div className={css(styleSheet.sidebarCenter)}>
								<h2 className={css(styleSheet.question)}>What would you like to do with this blog?</h2>
								<button type='button' className={css(baseStyleSheet.ctaButton)} onClick={() => handleScheduleClick()}>
									Schedule
								</button>
							</div>
						) : null}
						{/* We handle conitional rendering within the component, so we can manage the chat session. Don't wrap this in a conditional. */}
						<UniversalAssistantChatWithinComposer
							isOpen={showingAIAssistant}
							impersonationContext={impersonationContext?.toJs()}
							onClose={() => setShowingAIAssistant(false)}
							onUseBlogContent={onUseAIBlog}
							blogContext={{
								title: state.title,
								content: state.content.getRawRichTextContent(),
							}}
							emailContext={sourceType === 'email' && sourceContent ? (sourceContent as Api.EmailAIContext) : undefined}
							socialContext={
								sourceType === 'social' && sourceContent ? (sourceContent as Api.SocialMediaAIContext) : undefined
							}
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: state.showConfirmationModal,
								onRequestClose: onDeleteConfirmationRequestClose,
							}}
							options={DefaultDeleteConfirmationOptions}
							title='Are you sure you want to delete this blog post?'
						/>
					</>
				}
			>
				<BlogPostEditor
					onAIAssitantLinkClick={
						accountHasAiGeneratedContentFeatureEnabled ? () => setShowingAIAssistant(true) : undefined
					}
				/>
				<LeoFullscreenThinkingModal
					isOpen={blogGenerationMutation.isPending || Boolean(state.blogGenerationJobId)}
					text="I'm working on that blog post..."
				/>
			</BlogPostEditorLayout>
		</BlogPostEditorContext.Provider>
	);
}

export const CreateBlogPostFromTemplate = inject(ImpersonationContextKey)(observer(CreateBlogPostFromTemplateBase));
